import type {
    MediaItem,
} from "@mrflark/spl_service_model";
import type {
    ReactElement,
} from "preact/compat";
import {
    useEffect,
    useRef,
} from "preact/compat";

import {
    useViewerStore,
} from "../../stores/viewerStore.ts";

type FilmstripNavProps = {
    onNavigateToItem: (item: MediaItem) => void;
};

export const FilmstripNav = ({
    onNavigateToItem,
}: FilmstripNavProps): ReactElement | null => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const activeRef = useRef<HTMLButtonElement>(null);

    const {
        currentItem,
        browsingMode,
        orderedItems,
        orderedIndex,
        searchResults,
        searchResultsIndex,
        isSearchActive,
        recentItems,
        filmstripVisible,
    } = useViewerStore();

    if (!filmstripVisible) {
        return null;
    }

    let items: MediaItem[] = [];
    let activeIndex = -1;

    if (isSearchActive && searchResults.length > 0) {
        items = searchResults;
        activeIndex = searchResultsIndex;
    } else if (browsingMode !== "random" && orderedItems.length > 0) {
        items = orderedItems;
        activeIndex = orderedIndex;
    } else if (recentItems.length > 0) {
        items = recentItems;
        activeIndex = recentItems.findIndex((item) =>
            item.id === currentItem?.id
        );
    }

    useEffect(() => {
        if (activeRef.current) {
            activeRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "center",
            });
        }
    }, [activeIndex, currentItem?.id]);

    if (items.length < 2) {
        return null;
    }

    const windowSize = 15;
    let startIndex = 0;
    let visibleItems = items;
    if (items.length > windowSize && activeIndex >= 0) {
        const halfWindow = Math.floor(windowSize / 2);
        startIndex = Math.max(
            0,
            Math.min(activeIndex - halfWindow, items.length - windowSize),
        );
        visibleItems = items.slice(startIndex, startIndex + windowSize);
    }

    return (
        <div className="mt-3 w-full max-w-3xl">
            <div
                ref={scrollRef}
                className="flex gap-1.5 overflow-x-auto pb-1.5 scrollbar-thin"
            >
                {visibleItems.map((item, index) => {
                    const realIndex = startIndex + index;
                    const isActive = realIndex === activeIndex;

                    return (
                        <button
                            type="button"
                            key={item.id}
                            ref={isActive ? activeRef : undefined}
                            onClick={() => onNavigateToItem(item)}
                            className={`flex-shrink-0 rounded-md overflow-hidden transition-all ${
                                isActive
                                    ? "ring-2 ring-accent ring-offset-1 ring-offset-bg-primary"
                                    : "opacity-60 hover:opacity-100"
                            }`}
                        >
                            {item.media_type === "video"
                                ? (
                                    <video
                                        src={item.thumbnail_url ||
                                            item.media_url}
                                        className="w-16 h-16 object-cover"
                                        muted
                                    />
                                )
                                : (
                                    <img
                                        src={item.thumbnail_url ||
                                            item.media_url}
                                        alt=""
                                        className="w-16 h-16 object-cover"
                                        loading="lazy"
                                    />
                                )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
